import React, { useEffect, useState, useCallback, useRef } from 'react'
import AdminHOC from '../../../components/shared/HOC/AdminHOC'
import CustomButton from '../../../components/global/Button/CustomButton';
import {
    ExportOutlined, UserAddOutlined,
    ExclamationCircleOutlined, FileExcelOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CommonTable from '../../../components/shared/table/Table';
import usePagination from '../../../assets/hooks/PaginationHooks/usePagination';
import CustomTab from '../../../components/shared/tab/TabComponent';
import { TAB_LIST, BULK_UPLOAD_FORMAT_OPTIONS, TEMPLATE_FILES } from '../../../assets/FormFeilds/Config/config';
import { showErrorMessage } from '../../../components/shared/pagination/messages';
import { Table, message, Alert, Row, Col } from "antd";
import CustomUploadField from '../../../components/shared/new Form/Fields/CustomUploadFile';
import ConfirmDeletePopup from '../../../components/shared/confirmDeletePopup/ConfirmDeletePopup';
import UsersModal from './UsersModal';
import CustomSelector from '../../../components/shared/Input/Custom-Selector';
import { getUserColumns } from '../../../assets/FormFeilds/userField';
import CustomModal from '../../../components/global/CustomModal/CustomModal';
import { app } from "../../../service/serviceLMS";
import searchIcon from "../../../assets/Search-Icon.svg";
import CustomInput from '../../../components/global/CustomInput/CustomInput';
import LottieIcon from '../../../components/shared/lottieIcon/LottieIcon';
import { useUsers } from '../../../hooks/UserAdminHooks/UseUsers';
import "./UsersAdmin.css"
import useRoles from '../../../hooks/UserAdminHooks/useRoles';
import CustomLabelDropdown from '../../../components/global/CustomLabelDropdown/CustomLabelDropdown';
import UserBulkUploadModal from './components/UserBulkUploadModal';

const UserAdminPage = () => {

    const tableRef = useRef(null);
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth);
    const { currentPage, setCurrentPage, pageSize, setPageSize } = usePagination();
    const { userList, activeTotalElements, fetchUsers, removeUser } = useUsers();
    const {roles} = useRoles();
    const roleOptions = roles?.map(role => ({
    value: role,
    label: role
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase()),
    }));
    const [uiState, setUiState] = useState({
        activeTab: "1",
        isActive: true,
        isInputHovered: false,
        showLoader: false,
        searchText: "",
        visiblePopover: null,
        hoveredButton: null,
        selectedRole: null,
    });
    const [modalState, setModalState] = useState({
        isModalOpen: false,
        isAddModalOpen: false,
        isDownloadModalOpen: false,
        isValidationModalOpen: false,
        isConfirmPopupOpen: false,
    });

    const [userState, setUserState] = useState({
        selectedUser: null,
        userToDelete: null,
        loading: false,
    });

    const [uploadState, setUploadState] = useState({
        selectedFile: null,
        fileList: [],
        uploading: false,
        validating: false,
        validationResult: null,
        isValidationSuccessful: false,
        downloadFormat: null,
    });
    const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);


    const updateUI = useCallback((patch) => setUiState(prev => ({ ...prev, ...patch })), []);
    const updateModal = useCallback((patch) => setModalState(prev => ({ ...prev, ...patch })), []);
    const updateUser = useCallback((patch) => setUserState(prev => ({ ...prev, ...patch })), []);
    const updateUpload = useCallback((patch) => setUploadState(prev => ({ ...prev, ...patch })), []);

    
    const { activeTab, isActive, isInputHovered, showLoader, searchText, visiblePopover, hoveredButton, selectedRole } = uiState;
    const { isModalOpen, isAddModalOpen, isDownloadModalOpen, isValidationModalOpen, isConfirmPopupOpen } = modalState;
    const { selectedUser, userToDelete, loading } = userState;
    const { selectedFile, fileList, uploading, validating, validationResult, isValidationSuccessful, downloadFormat } = uploadState;

    const bulkUploadUsers = (formData) =>
        app.post("/user/api/client-api/admin/bulk-upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

    const validateUserUploadFile = (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return app.post("/user/api/client-api/admin/bulk-upload/validate", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    };

    const loadUsers = useCallback(async () => {
        updateUI({ showLoader: true });
        try {
            await fetchUsers({ isActive, currentPage, pageSize, searchText: searchText.trim(), selectedRole });
        } catch {
            showErrorMessage("Failed to load users.");
        } finally {
            updateUI({ showLoader: false });
        }
    }, [isActive, currentPage, pageSize, searchText, fetchUsers, selectedRole]);

    useEffect(() => {
        const handler = setTimeout(() => loadUsers(), 500);
        return () => clearTimeout(handler);
    }, [loadUsers]);

    
    const handleViewUserClick = useCallback((id) => {
    navigate(`/users/user-history/${id}`, {
        state: { isActive }
    });
}, [navigate, isActive]);

    const handleEditUser = useCallback((user) => {
        updateUser({ selectedUser: user });
        updateModal({ isModalOpen: true });
    }, []);

    const handleAddNew = useCallback(() => {
        updateUser({ selectedUser: null });
        updateModal({ isModalOpen: true, isAddModalOpen: false });
    }, []);

    const handleOpenConfirmDeletePopup = useCallback((user) => {
        updateUser({ userToDelete: user });
        updateModal({ isConfirmPopupOpen: true });
    }, []);

    const handleDeleteUser = async () => {
        try {
            updateUser({ loading: true });
            await removeUser(userToDelete?.userId);
            await loadUsers();
        } catch (err) {
            console.error(err);
        } finally {
            updateUser({ loading: false, userToDelete: null });
            updateModal({ isConfirmPopupOpen: false });
        }
    };

    const handleTabChange = useCallback((key) => {
        updateUI({ activeTab: key, isActive: key === "1", searchText: "" });
        setCurrentPage(1);
    }, [setCurrentPage]);

    const handleSearch = useCallback((query) => {
        updateUI({ searchText: query });
        setCurrentPage(1);
    }, [setCurrentPage]);

    const handleRoleChange = useCallback((role) => {
        updateUI({ selectedRole: role });
        setCurrentPage(1);
    }, [setCurrentPage]);

   
    const handleTableChange = useCallback((pagination) => {
        if (!pagination) return;
        if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize);
            setCurrentPage(1);
        } else {
            setCurrentPage(pagination.current);
        }
        const scrollableNode = tableRef.current?.querySelector('.ant-table-body');
        if (scrollableNode) scrollableNode.scrollTo({ top: 0, behavior: 'auto' });
    }, [pageSize, setPageSize, setCurrentPage]);

    const getTableProps = useCallback(() => {
        const columnConfig = {
            isActive, visiblePopover,
            setVisiblePopover: (v) => updateUI({ visiblePopover: v }),
            hoveredButton,
            setHoveredButton: (v) => updateUI({ hoveredButton: v }),
            handleViewUserClick, handleEditUser, handleOpenConfirmDeletePopup,isActive
        };
        const pagState = { current: currentPage, pageSize, total: activeTotalElements };
        switch (activeTab) {
            case "1": return { columns: getUserColumns(columnConfig), data: userList, pagState, onChange: handleTableChange };
            case "2": return { columns: getUserColumns(columnConfig), data: userList, pagState, onChange: handleTableChange };
            default:  return { columns: [], data: [], pagState: {}, onChange: () => {} };
        }
    }, [
        activeTab, isActive, visiblePopover, hoveredButton,
        currentPage, pageSize, activeTotalElements, userList,
        handleViewUserClick, handleEditUser, handleOpenConfirmDeletePopup, handleTableChange,
    ]);

    const tableProps = getTableProps();

    const resetUploadState = useCallback(() => {
        updateUpload({
            selectedFile: null,
            fileList: [],
            isValidationSuccessful: false,
            downloadFormat: null,
            validationResult: null,
        });
    }, []);

    const handleTemplateDownload = useCallback((format) => {
        updateUpload({ downloadFormat: format });
        updateModal({ isDownloadModalOpen: true });
    }, []);

    const confirmDownloadTemplate = useCallback(() => {
        if (!downloadFormat) return;
        const link = document.createElement("a");
        link.href = TEMPLATE_FILES[downloadFormat];
        link.download = `user_template.${downloadFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(`Template downloaded (${downloadFormat.toUpperCase()})`);
        updateModal({ isDownloadModalOpen: false });
        updateUpload({ downloadFormat: null });
    }, [downloadFormat]);


     const handleFileUploadSuccess = (parsedQuestionsData) => {
        console.log({parsedQuestionsData})
    setBulkUploadModalVisible(false);
    navigate("/users/bulk-review", {
    state: {
      parsedUsers: parsedQuestionsData,
    },
  });
  };

    const handleDownloadErrorCsv = useCallback(() => {
        if (!validationResult?.length) return;
        const headers = ["rowNumber", "firstName", "lastName", "email", "employeeId", "role", "errors"];
        const rows = validationResult.map((r) => [
            r.rowNumber, r.firstName, r.lastName,
            r.email, r.employeeId, r.role, r.errors.join("; "),
        ]);
        const csv = [
            headers.join(","),
            ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(",")),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "user_validation_errors.csv";
        link.click();
        URL.revokeObjectURL(url);
    }, [validationResult]);

    return (
        <>
            <section className="w-[100%] max-w-full h-[90vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
                <header className="flex flex-col gap-6 mb-6">
                    <div className="flex justify-between items-center h-[50px]">
                        <h1 className="text-[#E74860] text-[1.75rem] font-normal">
                            Employee Details
                        </h1>
                    </div>
                </header>

                <Row>
                    <Col span={6}>
                        <CustomTab entries={TAB_LIST} activeKey={activeTab} onChange={handleTabChange} />
                    </Col>
                    <Col span={18}>
                        <div className="flex justify-end gap-[10px] flex-wrap">
                            <div>
                            <CustomLabelDropdown handleSelectChange={handleRoleChange} options={roleOptions} allowClear  className="h-[46px]"  placeholder="Filter by Role" />
                            </div>
                            <div
                                onMouseEnter={() => updateUI({ isInputHovered: true })}
                                onMouseLeave={() => updateUI({ isInputHovered: false })}
                            >
                                <CustomInput
                                    key={activeTab}
                                    className="w-[14vw]"
                                    placeholder="Search"
                                    value={searchText}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    icon={
                                        isInputHovered
                                            ? <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                                            : <img src={searchIcon} alt="search" className="w-6 h-6" />
                                    }
                                    allowClear
                                />
                            </div>

                            <CustomButton
                                onClick={() => updateModal({ isAddModalOpen: true })}
                                variant="primary"
                                disabled={!isActive}
                            >
                                Add User
                            </CustomButton>

                            <CustomButton
                                onClick={() => navigate(`/users/user-report?status=${isActive ? "active" : "inactive"}`)}
                                variant="primary"
                                
                            >
                                User Report
                            </CustomButton>
                        </div>
                    </Col>
                </Row>

                <div ref={tableRef}>
                    <CommonTable
                        data={tableProps.data}
                        scroll={{ y: "48vh", x:"max-content" }}
                        className="hide-scrollbar top-table-radius"
                        showLoader={showLoader}
                        columns={tableProps.columns}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            total: activeTotalElements,
                            showSizeChanger: true,
                        }}
                        onChange={tableProps.onChange}
                        serverSide={true}
                        tableLayout="fixed" 
                    />
                </div>
            </section>

            <ConfirmDeletePopup
                isOpen={isConfirmPopupOpen}
                loading={loading}
                onClose={() => updateModal({ isConfirmPopupOpen: false })}
                onConfirm={handleDeleteUser}
            />
            <UsersModal
                key={selectedUser?.userId ?? 'new'}
                title={selectedUser ? "Edit Employee Details" : "Add New Employee"}
                isModalOpen={isModalOpen}
                getUserLists={loadUsers}
                handleCloseModal={() => updateModal({ isModalOpen: false })}
                selectedUser={selectedUser}
            />
            {/* <CustomModal
                title="Bulk Upload Users"
                visible={isDownloadModalOpen}
                width={600}
                onClose={() => { updateModal({ isDownloadModalOpen: false }); resetUploadState(); }}
            >
                <p className="text-[rgba(70,70,70,1)] text-sm mb-0">
                    Please ensure you enter all user details strictly in the format provided in the
                    downloaded template. Incorrect formatting may lead to errors during bulk upload.
                </p>

                <Table
                    size="small"
                    pagination={false}
                    bordered
                    dataSource={[
                        { key: 1, firstName: "John", lastName: "Doe", email: "john.doe@nucleusteq.com", employeeId: "EMP001", role: "EMPLOYEE" },
                        { key: 2, firstName: "Alice", lastName: "Johnson", email: "alice.johnson@nucleusteq.com", employeeId: "EMP002", role: "MANAGER" },
                    ]}
                    columns={[
                        { title: "First Name", dataIndex: "firstName" },
                        { title: "Last Name", dataIndex: "lastName" },
                        { title: "Email", dataIndex: "email" },
                        { title: "Employee Number", dataIndex: "employeeId" },
                        { title: "Role", dataIndex: "role" },
                    ]}
                    className="mt-4 mb-4"
                />

                <CustomUploadField
                    fileList={fileList}
                    maxCount={1}
                    accept={downloadFormat ? `.${downloadFormat}` : ".txt,.csv,.xlsx"}
                    handleFileChange={({ fileList: newFileList }) => {
                        if (newFileList.length === 0) resetUploadState();
                    }}
                    beforeUpload={handleFileValidation}
                    onRemove={resetUploadState}
                    disabled={false}
                />

                <div className="flex gap-2.5 mt-6 w-full">
                    <CustomButton
                        variant="primary"
                        onClick={handleUploadSubmit}
                        disabled={!selectedFile || !isValidationSuccessful}
                        loading={uploading}
                        style={{ width: "33%" }}
                    >
                        Upload File
                    </CustomButton>
                    <CustomButton
                        variant="primary"
                        onClick={confirmDownloadTemplate}
                        disabled={!downloadFormat}
                        style={{ width: "33%" }}
                    >
                        Download Template
                    </CustomButton>
                    <CustomButton
                        variant="outlined"
                        onClick={() => { updateModal({ isDownloadModalOpen: false }); resetUploadState(); }}
                        style={{ width: "33%" }}
                    >
                        Cancel
                    </CustomButton>
                </div>
            </CustomModal> */}

            <UserBulkUploadModal
                    bulkUploadModalVisible={bulkUploadModalVisible}
        setBulkUploadModalVisible={setBulkUploadModalVisible}
        onParsedDataSave={handleFileUploadSuccess}
             />

            <CustomModal
                visible={isAddModalOpen}
                onClose={() => updateModal({ isAddModalOpen: false })}
                title="Add User"
                destroyOnClose
            >
                <div className="flex flex-col gap-3">
                    <CustomButton
                        variant="outlined"
                        onClick={handleAddNew}
                        style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                        Add New User
                    </CustomButton>

                    <div className="flex flex-col gap-[7px]">
                        <CustomButton variant="outlined"
                            className="w-full flex justify-start "
                            onClick={() => { 
                    setBulkUploadModalVisible(true); 
                }}
                        >User Bulk Upload</CustomButton>
                        
                    </div>
                </div>
            </CustomModal>

            <CustomModal
                visible={isValidationModalOpen}
                title={
                    <span>
                        <ExclamationCircleOutlined className="text-[#faad14] mr-2" />
                        Selected File Validation Failed
                    </span>
                }
                onCancel={() => updateModal({ isValidationModalOpen: false })}
            >
                <Alert
                    type="warning"
                    message={`Validation failed. ${validationResult?.length ?? 0} error record(s) found.`}
                    description="Please review the errors in your file. You can download the error CSV to fix and re-upload."
                    showIcon
                    className="mb-4"
                />
                <div className="flex gap-3 p-2.5 justify-end">
                    {validationResult?.length > 0 && (
                        <CustomButton
                            style={{ width: "50%" }}
                            variant="primary"
                            icon={<FileExcelOutlined />}
                            onClick={handleDownloadErrorCsv}
                        >
                            Download Error CSV
                        </CustomButton>
                    )}
                    <CustomButton
                        style={{ width: "50%" }}
                        variant="outlined"
                        onClick={() => updateModal({ isValidationModalOpen: false })}
                    >
                        Close
                    </CustomButton>
                </div>
            </CustomModal>
        </>
    );
};

export default AdminHOC(UserAdminPage);